import { useEffect, useRef, useState } from "react";

const BADGE_TONE = {
  neutral: "bg-white/10 text-zinc-200",
  indigo: "bg-indigo-500/20 text-indigo-300",
  amber: "bg-amber-500/20 text-amber-300",
  red: "bg-red-500/20 text-red-300",
  warn: "bg-amber-500/20 text-amber-200",
  danger: "bg-red-500/20 text-red-200",
  good: "bg-emerald-500/20 text-emerald-200",
  info: "bg-sky-500/20 text-sky-200",
};

export const useSwipeTabs = (tabs, value, onChange) => {
  const start = useRef(null);
  return {
    onTouchStart: (e) => {
      if (window.innerWidth >= 1024 || e.touches.length !== 1) return;
      const t = e.touches[0];
      let el = e.target;
      while (el && el !== e.currentTarget) {
        const cs = window.getComputedStyle(el);
        if (/(auto|scroll)/.test(cs.overflowX) && el.scrollWidth > el.clientWidth + 2) return;
        if (/^(INPUT|TEXTAREA|SELECT|CANVAS)$/.test(el.tagName) || el.isContentEditable) return;
        el = el.parentElement;
      }
      start.current = { x: t.clientX, y: t.clientY, at: Date.now() };
    },
    onTouchEnd: (e) => {
      const s = start.current;
      start.current = null;
      if (!s) return;
      const t = e.changedTouches[0];
      const dx = t.clientX - s.x;
      const dy = t.clientY - s.y;
      if (Math.abs(dx) < 70 || Math.abs(dx) < Math.abs(dy) * 1.6 || Date.now() - s.at > 700) return;
      const keys = tabs.filter((x) => !x.to).map((x) => x.key);
      const i = keys.indexOf(value);
      const next = keys[dx < 0 ? i + 1 : i - 1];
      if (i < 0 || !next) return;
      onChange(tabs.find((x) => x.key === next));
    },
  };
};

export const SegTabs = ({ tabs, value, onChange, testId, className = "", size = "md", sticky = false }) => {
  const stripRef = useRef(null);
  const [edges, setEdges] = useState({ left: false, right: false });

  const measure = () => {
    const el = stripRef.current;
    if (!el) return;
    setEdges({
      left: el.scrollLeft > 4,
      right: el.scrollLeft + el.clientWidth < el.scrollWidth - 4,
    });
  };

  useEffect(() => {
    measure();
    const el = stripRef.current;
    if (!el) return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [tabs.length]);

  useEffect(() => {
    const el = stripRef.current?.querySelector(`[data-key="${value}"]`);
    el?.scrollIntoView({ block: "nearest", inline: "center", behavior: "smooth" });
  }, [value]);

  return (
    <div className={`seg-wrap ${sticky ? "is-sticky" : ""} ${className}`} data-testid={testId} data-size={size}>
      <div className="seg-tabs" role="tablist" ref={stripRef} onScroll={measure}>
        {tabs.map((t) => {
          const active = value === t.key;
          const Icon = t.Icon || t.icon;
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              data-key={t.key}
              aria-selected={active}
              data-testid={t.testId || `${testId ? testId.replace(/-tabs$/, "") : "seg"}-tab-${t.key}`}
              title={t.hint}
              onClick={() => onChange(t)}
              className={`seg-tab ${active ? "is-active" : ""}`}
            >
              {Icon && <Icon size={15} weight={active ? "fill" : "bold"} />}
              <span>{t.label}</span>
              {t.badge != null && t.badge !== 0 && (
                <span
                  data-testid={`${testId ? testId.replace(/-tabs$/, "") : "seg"}-tab-badge-${t.key}`}
                  data-tone={t.tone || "neutral"}
                  className={`seg-badge ${BADGE_TONE[t.tone] || BADGE_TONE.neutral}`}
                >
                  {t.badge}
                </span>
              )}
              {t.dot && <span className="seg-dot" />}
            </button>
          );
        })}
      </div>
      <span className={`seg-fade seg-fade-l ${edges.left ? "is-on" : ""}`} aria-hidden />
      <span className={`seg-fade seg-fade-r ${edges.right ? "is-on" : ""}`} aria-hidden />
    </div>
  );
};
